import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAuthenticateUserMutation } from "../api/userAuthApi";
import { updateWpRestNonce } from "../../../app/api"; // Import the nonce updater
import type {
  LoginPayload,
  RegisterPayload,
  AuthSecurityPayload,
} from "../types/userAuthApiTypes";

export const useUserAuth = () => {
  const { t } = useTranslation();
  const [authenticate, { isLoading }] = useAuthenticateUserMutation();

  const handleAuth = async (
    args:
      | ({ type: "login" } & LoginPayload & AuthSecurityPayload)
      | ({ type: "register" } & RegisterPayload & AuthSecurityPayload)
  ) => {
    // Default messages
    const defaults = {
      loading:
        args.type === "login"
          ? t("user.auth.loginButtonLoading", "Logging in...")
          : t("user.auth.registerButtonLoading", "Creating account..."),
      success:
        args.type === "login"
          ? t("feedback.user.login.success", "Login successful!")
          : t("feedback.user.register.success", "Account created!"),
      error:
        args.type === "login"
          ? t("feedback.user.login.error", "Login failed.")
          : t("feedback.user.register.error", "Registration failed."),
    };

    const toastId = toast.loading(defaults.loading);

    try {
      const response = await authenticate(args).unwrap();

      // Failsafe: Explicitly update nonce if returned in body (headers are handled by middleware)
      if (response.nonce) {
        updateWpRestNonce(response.nonce);
      }

      toast.success(defaults.success, { id: toastId });
      return { success: true, data: response };
    } catch (err: any) {
      const errorCode = err?.data?.code;
      let errorMessage = defaults.error;

      // Map backend error codes to frontend translations
      // We map BOTH hyphenated and underscored versions for robustness.
      const errorMap: Record<string, string> = {
        // Login Errors - Standard WP
        incorrect_password: t(
          "feedback.user.auth.invalidCredentials",
          "Invalid username or password."
        ),
        invalid_username: t(
          "feedback.user.auth.invalidCredentials", // Keep generic for security
          "Invalid username or password."
        ),
        authentication_failed: t(
          "feedback.user.auth.invalidCredentials",
          "Invalid username or password."
        ),
        // Login Errors - Security Plugins
        too_many_retries: t(
          "feedback.user.auth.tooManyAttempts",
          "Too many failed login attempts. Please try again later."
        ),
        // Fallback custom error code
        invalid_credentials: t(
          "feedback.user.auth.invalidCredentials",
          "Invalid username or password."
        ),
        "invalid-credentials": t(
          "feedback.user.auth.invalidCredentials",
          "Invalid username or password."
        ),

        // Register Errors
        registration_error_email_exists: t(
          "feedback.user.auth.emailExists",
          "An account is already registered with your email address."
        ),
        "registration-error-email-exists": t(
          "feedback.user.auth.emailExists",
          "An account is already registered with your email address."
        ),
        registration_error_username_exists: t(
          "feedback.user.auth.usernameExists",
          "This username is already taken."
        ),
        "registration-error-username-exists": t(
          "feedback.user.auth.usernameExists",
          "This username is already taken."
        ),
        invalid_email_format: t(
          "feedback.user.auth.invalidEmailFormat",
          "Please provide a valid email address."
        ),
        missing_password: t(
          "feedback.user.auth.missingPassword",
          "Password is required."
        ),
        weak_password: t(
          "user.validation.weakPassword",
          "Password must be at least 8 characters long and include an uppercase letter and a number."
        ),
        "weak-password": t(
          "user.validation.weakPassword",
          "Password must be at least 8 characters long and include an uppercase letter and a number."
        ),
      };

      if (errorCode && errorMap[errorCode]) {
        errorMessage = errorMap[errorCode];
      }

      toast.error(errorMessage, { id: toastId });
      return { success: false, error: err };
    }
  };

  return { handleAuth, isLoading };
};
